import React from 'react';
import { useDevelopmentDemo } from '../../contexts/DevelopmentDemoContext';
import { DemoSectionHeader } from '../demoShell/DemoSectionHeader';
import { formatTimestamp } from '../../utils/demoStorage';
import { glassPanel } from '../../utils/demoUi';
import { decisionLabels, deriveStage, stageLabels, submissionsFor } from '../../utils/developmentDemo';
import { ParticipantPicker } from './ParticipantPicker';
import { ResponseRecord } from './ResponseRecord';
export function ParticipantRecords() {
  const {
    state,
    config
  } = useDevelopmentDemo();
  const participantId = state.ui.selectedParticipantId;
  const participant = config.participants.find(item => item.id === participantId);
  const submissions = submissionsFor(state, participantId);
  const activities = state.activities.filter(activity => activity.participantId === participantId);
  const decisions = state.decisions.filter(decision => decision.participantId === participantId);
  const stage = deriveStage(state, participantId);
  return <div data-id="element-833" data-mp="3753h9ytxfsr1">
      <DemoSectionHeader title="Participant Records" description="Every submission, review and activity is kept. Revisions add new records rather than replacing earlier ones." data-id="element-834" data-mp="375bphdf3u9vw" />
      <ParticipantPicker data-id="element-835" data-mp="3h1ybw6n2x901" />

      <section aria-labelledby="record-title" className={`${glassPanel} p-6`} data-id="element-836" data-mp="3h1on2mdrt7vx">
        <p className="font-mono text-xs font-semibold text-ink-muted" data-id="element-837" data-mp="3l4bfaoecsutn">{participantId} · fictional record</p>
        <h2 id="record-title" className="mt-1 font-heading text-2xl font-bold text-ink-dark" data-id="element-838" data-mp="3q24o7p71k2xm">
          {participant?.name}
        </h2>
        <p className="mt-1 text-ink-muted" data-id="element-839" data-mp="3v062lwtive2r">{participant?.description}</p>
        <p className="mt-3 text-ink-dark" data-id="element-840" data-mp="3owedujc5zjb">
          <span className="font-semibold" data-id="element-841" data-mp="8modb8ieelx0">Current stage: </span>
          {stageLabels[stage].label} — {stageLabels[stage].reviewer}
        </p>
      </section>

      <section aria-labelledby="record-submissions" className="mt-8" data-id="element-842" data-mp="cpuj6f1lmb2y">
        <h2 id="record-submissions" className="font-heading text-lg font-bold text-ink-dark" data-id="element-843" data-mp="hmkyv19hr4nu">
          Submissions and reviews ({submissions.length})
        </h2>
        {submissions.length === 0 ? <p className="mt-2 text-sm text-ink-muted" data-id="element-844" data-mp="29pxb3lcpcgjj">No responses submitted yet.</p> : <ul className="mt-3 space-y-4" data-id="element-845" data-mp="2nhc6krnsqa7h">
            {[...submissions].reverse().map(submission => <li key={submission.id} data-id="element-846" data-mp="2ioiypux16lr6">
                <ResponseRecord submission={submission} data-id="element-847" data-mp="2inhh17lif10h" />
              </li>)}
          </ul>}
      </section>

      <section aria-labelledby="record-activities" className="mt-8" data-id="element-848" data-mp="2sfncwsevu2ky">
        <h2 id="record-activities" className="font-heading text-lg font-bold text-ink-dark" data-id="element-849" data-mp="2shq43frru1r9">
          Development activities ({activities.length})
        </h2>
        {activities.length === 0 ? <p className="mt-2 text-sm text-ink-muted" data-id="element-850" data-mp="31e8uolcj5dko">No activities assigned.</p> : <ul className="mt-3 divide-y divide-line border-y border-line" data-id="element-851" data-mp="1c3840x357dqt">
            {activities.map(activity => <li key={activity.id} className="py-3" data-id="element-852" data-mp="3ipe6u2y2hqka">
                <p className="font-semibold text-ink-dark" data-id="element-853" data-mp="3ipxl99oidwtk">
                  {activity.title}{' '}
                  <span className="text-sm font-normal text-ink-muted" data-id="element-854" data-mp="3nnz7g3h8ec99">
                    · {activity.id} from {activity.reviewId} · {activity.status === 'completed' ? 'Completed' : 'In Progress'}
                  </span>
                </p>
                {activity.reflection && <p className="mt-1 whitespace-pre-wrap text-sm text-ink-muted" data-id="element-855" data-mp="2zymx30a63y5d">Reflection: {activity.reflection}</p>}
              </li>)}
          </ul>}
      </section>

      {config.features.comparison && <section aria-labelledby="record-decisions" className="mt-8" data-id="element-856" data-mp="2qzsvevhzchky">
          <h2 id="record-decisions" className="font-heading text-lg font-bold text-ink-dark" data-id="element-857" data-mp="2uz1p60d0sfgi">
            Human decisions ({decisions.length})
          </h2>
          <p className="mt-1 text-sm text-ink-muted" data-id="element-858" data-mp="p50mpzrsgn2s">Recorded separately from development findings.</p>
          {decisions.length === 0 ? <p className="mt-2 text-sm text-ink-muted" data-id="element-859" data-mp="4i2bgrcl3nqx">No decisions recorded. Use Candidate Comparison to record one.</p> : <ul className="mt-3 divide-y divide-line border-y border-line" data-id="element-860" data-mp="3vovb3qrqbetc">
              {[...decisions].reverse().map(decision => <li key={decision.id} className="py-3 text-sm" data-id="element-861" data-mp="3lq63qrxglcrc">
                  <p className="font-semibold text-ink-dark" data-id="element-862" data-mp="3ls0hnmabc4lx">
                    {decisionLabels[decision.outcome]} <span className="font-normal text-ink-muted" data-id="element-863" data-mp="3hqf7i8p5y4ff">· {decision.id} · {formatTimestamp(decision.at)}</span>
                  </p>
                  <p className="mt-0.5 text-ink-muted" data-id="element-864" data-mp="37xzrvi1c9rxp">
                    Reason: {decision.reason} · Evidence: {decision.reviewIds.join(', ')}
                  </p>
                </li>)}
            </ul>}
        </section>}
    </div>;
}